<template>
  <b-modal
    id="talk-select-modal"
    hide-header
    hide-footer
    centered
    scrollable
    content-class="talk-select-modal-content"
    body-class="talk-select-modal-body"
    @show="showModal"
  >
    <template #default="{ close }">
      <div class="mb-1">トーク</div>

      <!-- 絞り込み -->
      <div class="filter-incremental">
        <b-input-group>
          <b-icon-search class="search-icon" />
          <input
            id="filter-incremental-input"
            v-model="filterIncremental"
            type="text"
            class="form-control custom-input filter-incremental-input"
            placeholder="インクリメンタル絞込"
          />
          <!-- クリアボタン -->
          <a
            v-if="filterIncremental !== ''"
            href="#"
            class="clear-button"
            @click.prevent="clickClear"
          >
            <img src="../../assets/clear_button.svg" />
          </a>
        </b-input-group>

        <!-- 並び順ボタン -->
        <b-button
          variant="outline-secondary"
          class="filter-incremental-button"
          @click="showSortOrder = true"
        >
          <img src="../../assets/filter_list.svg" />
        </b-button>
      </div>

      <!-- 並び順ダイアログ -->
      <sort-order-dialog
        v-if="showSortOrder"
        :value="sortConfig"
        @change-value="changeSortConfig"
        @close="showSortOrder = false"
      />

      <!--選択中のトーク -->
      <div class="selected-talk my-1">
        <span class="selected-label">選択中</span>
        <div class="selected-talk-name my-1">
          <span class="talk-name">{{ selectedTalk.display_name }}</span>
          <span v-if="selectedTalk.user_count_str">{{ selectedTalk.user_count_str }}</span>
          <b-icon-star-fill v-if="selectedTalk.is_favorite" class="favorite-icon" />
          <span v-if="selectedTalk.show_open_talk_icon" class="open-talk-icon">
            <img src="../../assets/icon_openchat.svg" />
          </span>
        </div>

        <hr class="m-0" />
      </div>

      <!-- トークリスト -->
      <div class="talk-list">
        <b-form-radio-group
          name="talk-radio-group"
          v-model="selectedTalkId"
          value-field="id"
          stacked
        >
          <!-- なし -->
          <b-form-radio
            :class="{
              'talk-select-radio-button': true,
              hide: !isVisibleRadioOption(talkNone),
            }"
            :value="talkNone.talk_id_str"
          >
            <div
              class="talk-label"
              @mouseenter="onMouseEnterRadioOption(talkNone)"
              @mouseleave="onMouseLeaveRadioOption"
            >
              <span :id="`popover-${talkNone.talk_id_str}`" class="talk-name">
                {{ talkNone.display_name }}
              </span>
            </div>
          </b-form-radio>

          <!-- トークのラジオボタン -->
          <b-form-radio
            v-for="talk in infinite.list"
            :class="{
              'talk-select-radio-button': true,
              hide: !isVisibleRadioOption(talk),
            }"
            :key="talk.talk_id_str"
            :value="talk.talk_id_str"
          >
            <div
              class="talk-label"
              @mouseenter="onMouseEnterRadioOption(talk)"
              @mouseleave="onMouseLeaveRadioOption"
            >
              <span :id="`popover-${talk.talk_id_str}`" class="talk-name">
                {{ talk.display_name }}
              </span>
              <span v-if="talk.user_count_str">{{ talk.user_count_str }}</span>
              <b-icon-star-fill v-if="talk.is_favorite" class="favorite-icon" />
              <span v-if="talk.show_open_talk_icon" class="open-talk-icon">
                <img src="../../assets/icon_openchat.svg" />
              </span>
            </div>
          </b-form-radio>

          <!-- マウスホバーで表示するトーク名のpopover -->
          <b-popover
            v-if="popoverTalk"
            :target="`popover-${popoverTalk.talk_id_str}`"
            :title="popoverTitle"
            :show.sync="showPopover"
            triggers="manual"
            placement="bottom"
            boundary="viewport"
          />
        </b-form-radio-group>

        <infinite-loading :identifier="infinite.identifier" @infinite="infiniteHandler">
          <div slot="spinner"></div>
          <div slot="no-results"></div>
          <div slot="no-more"></div>
        </infinite-loading>
      </div>

      <!-- ボタン -->
      <div class="d-flex justify-content-center mt-1">
        <cancel label="キャンセル" v-on:cancel="close" />
        <confirm label="決定" v-on:confirm="done(close)" class="ml-2" />
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, reactive, ref, watch, type PropType } from "vue";
import { useAppStore } from "@/store";
import { TALK_NONE, Talk } from "@/model/talk";
import Confirm from "./../button/Confirm.vue";
import Cancel from "./../button/Cancel.vue";
import SortOrderDialog from "./SortOrderDialog.vue";
import { sortTalks, isSameSortConfig, DEFAULT_SORT_CONFIG, type SortConfig } from "./sort-talks";
import "./talk-select-modal.scss";

type Infinite = {
  list: Talk[];
  startCnt: number;
  loadCnt: number;
  identifier: number;
  change: () => void;
};

export default defineComponent({
  name: "talk-select-modal",
  components: { Confirm, Cancel, SortOrderDialog },
  props: {
    /** トーク一覧 */
    talks: { type: Array as PropType<Talk[]>, default: () => [] },
    /** 初期選択トークのid */
    defaultTalkId: { type: String, default: "" },
  },
  setup(props, { emit }) {
    // 「なし」の選択項目
    const talkNone: Partial<Talk> = TALK_NONE;

    // インクリメンタル絞り込み
    const filterIncremental = ref("");

    // 並び順ダイアログ表示フラグ
    const showSortOrder = ref(false);

    // 並び順設定
    const sortConfig = ref<SortConfig>({ ...DEFAULT_SORT_CONFIG });

    // 選択中トークID
    const selectedTalkId = ref<string>(TALK_NONE.talk_id_str!);

    // popoverに表示するトーク
    const popoverTalk = ref<Partial<Talk> | null>(null);

    // 無限スクロール用の表示情報
    const infinite = reactive<Infinite>({
      list: [],
      startCnt: 0,
      loadCnt: 100, // ロード件数
      identifier: 0,
      change: () => {
        infinite.list = [];
        infinite.startCnt = 0;
        infinite.identifier++;
      },
    });

    // トークリスト
    const talkList = computed((): Talk[] => {
      return sortTalks(props.talks, sortConfig.value);
    });

    // 選択中トーク
    const selectedTalk = computed((): Partial<Talk> => {
      const selected = talkList.value.find((talk) => talk.talk_id_str == selectedTalkId.value);
      return selected ?? TALK_NONE;
    });

    // モバイルかどうか
    const isMobile = computed((): boolean => useAppStore().isMobile);

    // トーク名popover表示フラグ
    const showPopover = computed((): boolean => {
      // モバイルでは表示しない
      return !isMobile.value && popoverTalk.value != null;
    });

    const MAX_DISPLAY_NAME_LENGTH = 300;

    // popoverに表示するタイトル
    // （トーク名が300文字を超える場合は省略し、オープントークは「[オープントーク] 」を前置する）
    const popoverTitle = computed((): string => {
      const talk = popoverTalk.value;
      if (!talk) {
        return "";
      }
      const name = talk.display_name ?? "";
      const truncated =
        name.length > MAX_DISPLAY_NAME_LENGTH ? name.slice(0, MAX_DISPLAY_NAME_LENGTH) + "⋯" : name;
      const title = `${truncated}${talk.user_count_str ?? ""}`;
      return talk.show_open_talk_icon ? `[オープントーク] ${title}` : title;
    });

    // トークリスト変更時
    watch(
      () => talkList.value,
      () => {
        // 無限スクロール初期化
        infinite.change();
      }
    );

    // 絞り込み変更時
    watch(
      () => filterIncremental.value,
      () => {
        // 無限スクロール初期化
        infinite.change();
      }
    );

    // 表示時の処理
    const showModal = (): void => {
      // インクリメンタル絞り込み初期化
      filterIncremental.value = "";

      // 無限スクロール初期化
      infinite.change();

      // 初期選択トーク
      const exist = talkList.value.some((talk) => talk.talk_id_str == props.defaultTalkId);
      selectedTalkId.value = exist ? props.defaultTalkId : TALK_NONE.talk_id_str!;
    };

    // 無限スクロールのデータ読み込み
    const infiniteHandler = (state: import("vue-infinite-loading").StateChanger): void => {
      const list = talkList.value;
      if (list.length > infinite.startCnt) {
        // 描画されていないデータがある > 表示範囲拡大
        infinite.list.push(...list.slice(infinite.startCnt, infinite.startCnt + infinite.loadCnt));
        infinite.startCnt += infinite.loadCnt;
        // 連続実行を防ぐため、DOMの更新後に読み込み完了する
        nextTick(() => {
          state.loaded();
        });
      } else {
        // 全て描画されている
        state.complete();
      }
    };

    // トークの表示切替
    const isVisibleRadioOption = (talk: Partial<Talk>): boolean => {
      if (filterIncremental.value) {
        const name = talk.display_name ?? "";
        return name.includes(filterIncremental.value);
      } else {
        return true;
      }
    };

    // ラジオボタンのmouseenter
    const onMouseEnterRadioOption = (talk: Partial<Talk>): void => {
      popoverTalk.value = talk;
    };

    // ラジオボタンのmouseleave
    const onMouseLeaveRadioOption = (): void => {
      popoverTalk.value = null;
    };

    // 絞り込み条件をクリア
    const clickClear = (): void => {
      filterIncremental.value = "";
    };

    // 並び順設定の変更（同一設定なら無限スクロールをリセットしない）
    const changeSortConfig = (config: SortConfig): void => {
      if (isSameSortConfig(config, sortConfig.value)) {
        return;
      }
      sortConfig.value = config;
    };

    // 決定時の処理
    const done = (close: () => CloseEvent): void => {
      emit("selected", selectedTalk.value);
      close();
    };

    return {
      talkNone,
      filterIncremental,
      showSortOrder,
      sortConfig,
      selectedTalkId,
      popoverTalk,
      popoverTitle,
      infinite,
      talkList,
      selectedTalk,
      isMobile,
      showPopover,
      showModal,
      infiniteHandler,
      isVisibleRadioOption,
      onMouseEnterRadioOption,
      onMouseLeaveRadioOption,
      clickClear,
      changeSortConfig,
      done,
    };
  },
});
</script>
